import axios from "axios";
import {deleteJWT, getJWT} from "./security/jwt";
import {refreshToken} from "./security/authenticator";
import debug from "./debug";
import {queryString} from "./env";
import {deleteAuthkey} from "./user";

/**
 * Set location
 *
 * @param url {String|null}
 */
const setLocation = (url = null) => {
  if (this && this.url) {
    window.location.href = this.url;
  } else {
    window.location.href = url;
  }
};

/**
 * Init URL params
 *
 * @return {Object}
 */
const initURLParams = () => {
  const urlParams = {};
  if (queryString.length) {
    let query = queryString.slice(1).split(/=/);
    for (var i = 0; i < query.length; i += 2) {
      urlParams[query[i]] = query[i + 1];
    }
  }

  return urlParams;
};
const urlParams = initURLParams();

/**
 * Axios setup interceptors and base url
 */
const axiosSetup = () => {
  axios.defaults.baseURL = API_V3_HOST;

  // Attach JWT before sending the request
  axios.interceptors.request.use(
    config => {
      const token = getJWT();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    },
  );

  // Add a response interceptor to check if token not valid when sending a request
  axios.interceptors.response.use(
    response => response,
    async error => {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      const originalRequest = error.config;
      if (
        error.response.status === 401 &&
        originalRequest.url.includes("/account/refreshToken")
      ) {
        // Clean user data
        deleteJWT();
        deleteAuthkey();
        // Redirect to login
        setLocation(publicPath + "/login");

        // Mark as error
        return Promise.reject(error);
      } else if (error.response.status === 401 && !originalRequest._retry) {
        if (originalRequest.url.includes("/account/resetPassword") || originalRequest.url.includes("/account/setup") || originalRequest.url.includes("/account/demo/clearCredentials")) {
          // Do not refresh token when resetting password or setting up account
          return Promise.reject(error);
        }
        // Retry
        originalRequest._retry = true;

        // Refresh token
        await refreshToken();

        return axios(originalRequest);
      }

      return Promise.reject(error);
    },
  );
};
axiosSetup();

/**
 * Get URL param
 *
 * @param name {String}
 * @return {null|*} Null if not defined
 */
const getURLParam = name => {
  if ("undefined" === typeof (urlParams[name])) {
    return null;
  }

  return urlParams[name];
};

/**
 * Get a query string from some data
 *
 * @param data {Object}
 * @return {String}
 */
const getQueryStringFromData = data => {
  if (!data || "object" !== typeof (query) || !Object.keys(data).length) {
    return "";
  }

  let query = [], key;
  for (key in data) {
    query.push(key + "=" + encodeURIComponent(data[key]));
  }

  return "?" + query.join("&");
};

/**
 * Make an HTTP POST request
 * @param url {String}
 * @param data {*} Anything to JSON serialize or null if empty data to send
 * @param progressHandler {function|null}
 * @return {Promise<Response>}
 */
const httpPost = (url, data, progressHandler = null) => {
  const headers = {
    "Content-Type": "application/json",
  };

  if (progressHandler && "function" == typeof (progressHandler)) {
    progressHandler();
  }

  return fetch(
    url,
    {
      method: "POST",
      headers,
      mode: "cors",
      body: data ? JSON.stringify(data) : null,
    },
  )
    .then(r => {
      debug(["Response received", r]);

      return r;
    })
    .catch(r => {
      debug(["Error when requesting (POST)", url], ["With error", r]);
    });
};

/**
 * Make an HTTP GET request
 * @param url {String}
 * @param data {Object|null} Object with data to send as query string
 * @param progressHandler {function|null}
 * @return {Promise<Response>}
 */
const httpGet = (url, data = null, progressHandler = null) => {
  const headers = {
    "Content-Type": "application/json",
  };

  let query = data;
  if ("object" === typeof (query)) {
    query = getQueryStringFromData(data);
  }

  if (progressHandler && "function" == typeof (progressHandler)) {
    progressHandler();
  }

  return fetch(
    url + (query ? query : ""),
    {
      method: "GET",
      headers,
      mode: "cors",
    },
  )
    .then(r => {
      debug(["Response received", r]);

      return r;
    })
    .catch(r => {
      debug(["Error when requesting (GET)", url], ["With error", r]);
    });
};

/**
 * HTTP GET to APIv3
 *
 * @param url {String}
 * @param headers {Object}
 *
 * @return {Promise}
 */
const httpV3Get = (url, headers = {}) => {
  return axios.get(url, {headers, responseType: "json"});
};

/**
 * HTTP POST to APIv3
 *
 * @param url {String}
 * @param data {Object|null}
 * @param headers {Object}
 *
 * @return {Promise}
 */
const httpV3Post = (url, data = null, headers = {}) => {
  return axios.post(url, data, {headers, responseType: "json"});
};

export {setLocation, getURLParam, httpPost, httpGet, httpV3Post, httpV3Get};

export default httpPost;
